import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { Loader2, Crown, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";

function BigStat({ label, value, deltaPct, testId }) {
  const showDelta = deltaPct !== null && deltaPct !== undefined;
  const positive = showDelta && deltaPct > 0;
  const negative = showDelta && deltaPct < 0;
  return (
    <div className="rounded-xl border border-white/8 bg-[#141414] px-4 py-3.5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-display text-2xl text-white tabular-nums" data-testid={testId}>{value}</div>
        {showDelta && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] tabular-nums ${positive ? "text-emerald-300" : negative ? "text-rose-300" : "text-white/40"}`}>
            {positive ? <TrendingUp className="w-3 h-3" /> : negative ? <TrendingDown className="w-3 h-3" /> : null}
            {positive ? "+" : ""}{deltaPct}%
          </span>
        )}
      </div>
      {showDelta && <div className="text-[10px] text-white/35 mt-0.5">vs previous cycle</div>}
    </div>
  );
}

/**
 * Enterprise Phase 3 polish — workspace usage analytics.
 *
 * Renders the per-cycle ROI dashboard:
 *   • 3 hero stat tiles (Credits · Runs · Active members) with previous-cycle delta
 *   • Cycle window pill (or "Last 30 days" when never_paid)
 *   • Category breakdown bar list (Studio render / Catalog compose / CAD …)
 *   • Members table sorted by spend with FREELOADER pills
 */
export default function TeamAnalyticsTab({ workspaceId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/workspaces/${workspaceId}/analytics`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err, "Could not load analytics."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loading || !data) {
    return (
      <div className="max-w-5xl mt-8 text-sm text-white/55" data-testid="team-analytics-loading">
        <Loader2 className="w-4 h-4 inline mr-2 animate-spin text-[#D4AF37]" /> Loading analytics…
      </div>
    );
  }

  const { totals, window: win, members, categories, thresholds } = data;
  const fmtCredits = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
  const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const fmtRelative = (iso) => {
    if (!iso) return "Never";
    const diffH = (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60);
    if (diffH < 1) return "Just now";
    if (diffH < 24) return `${Math.floor(diffH)}h ago`;
    const d = Math.floor(diffH / 24);
    return d < 30 ? `${d}d ago` : new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };
  const deltaPct = totals.delta_pct;
  const maxCategoryCredits = Math.max(1, ...categories.map((c) => c.credits));

  return (
    <div className="max-w-5xl mt-8 space-y-6" data-testid="team-analytics-tab">
      {/* Cycle window + 3 hero tiles */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#D4AF37]/8 via-[#1A1306] to-[#0B0B0B] p-6 sm:p-7">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">This cycle</div>
          <div className="text-xs text-white/55 tabular-nums" data-testid="team-analytics-window">
            {win.cycle_locked ? "Cycle" : "Last 30 days"} · {fmtDate(win.start)} – {fmtDate(win.end)}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BigStat
            label="Credits spent"
            value={fmtCredits(totals.credits_spent)}
            deltaPct={deltaPct}
            testId="team-analytics-stat-credits"
          />
          <BigStat
            label="Runs"
            value={totals.runs.toLocaleString("en-IN")}
            testId="team-analytics-stat-runs"
          />
          <BigStat
            label="Active members"
            value={`${totals.active_members} / ${members.length}`}
            testId="team-analytics-stat-active"
          />
        </div>
      </div>

      {/* Category breakdown */}
      <div className="rounded-2xl border border-white/10 bg-[#0E0E0E] p-6" data-testid="team-analytics-categories">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Where the credits went</div>
            <div className="font-display text-lg text-white mt-0.5">Action breakdown</div>
          </div>
        </div>
        {categories.length === 0 ? (
          <div className="text-sm text-white/45 py-4">No spend in this window yet.</div>
        ) : (
          <div className="space-y-2.5">
            {categories.map((c) => (
              <div key={c.label} data-testid={`team-analytics-cat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-white/85">{c.label}</span>
                  <span className="text-white/55 tabular-nums">
                    {fmtCredits(c.credits)} credits · {c.runs} {c.runs === 1 ? "run" : "runs"}
                    <span className="text-white/40"> · {Math.round((c.share || 0) * 100)}%</span>
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F3CE5A]"
                    style={{ width: `${Math.max(2, (c.credits / maxCategoryCredits) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Members table */}
      <div className="rounded-2xl border border-white/10 bg-[#0E0E0E] p-6" data-testid="team-analytics-members">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Per-member usage</div>
            <div className="font-display text-lg text-white mt-0.5">Designers ranked by spend</div>
          </div>
          <div className="text-[10.5px] text-white/40">
            Freeloader = &lt; {thresholds.freeloader_min_runs} runs OR &lt; {Math.round(thresholds.freeloader_min_share * 100)}% share
          </div>
        </div>

        {members.length === 0 ? (
          <div className="text-sm text-white/45 py-4">No members yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-white/45 text-left border-b border-white/8">
                  <th className="py-2 pr-3 font-normal">Member</th>
                  <th className="py-2 px-3 font-normal text-right">Credits</th>
                  <th className="py-2 px-3 font-normal text-right">Runs</th>
                  <th className="py-2 px-3 font-normal text-right">Share</th>
                  <th className="py-2 pl-3 font-normal text-right">Last active</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.user_id} className="border-b border-white/5 last:border-0" data-testid={`team-analytics-row-${m.user_id}`}>
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {m.role === "lead" && <Crown className="w-3 h-3 text-[#D4AF37] shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-white truncate">{m.name}</div>
                          <div className="text-[10px] text-white/40 truncate">{m.email}</div>
                        </div>
                        {m.freeloader && m.role !== "lead" && (
                          <span
                            data-testid={`team-analytics-freeloader-${m.user_id}`}
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-rose-300 bg-rose-500/15 border border-rose-400/30 rounded-full px-1.5 py-0.5 shrink-0"
                            title="Low utilization — under the freeloader threshold"
                          >
                            <AlertTriangle className="w-2.5 h-2.5" /> Freeloader
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right text-white/85 tabular-nums" data-testid={`team-analytics-row-${m.user_id}-credits`}>
                      {fmtCredits(m.credits_spent)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-white/85 tabular-nums">{m.runs}</td>
                    <td className="py-2.5 px-3 text-right text-white/65 tabular-nums">{Math.round((m.share || 0) * 100)}%</td>
                    <td className="py-2.5 pl-3 text-right text-white/55 tabular-nums">{fmtRelative(m.last_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
